import React from 'react';
import { useStyles } from 'uinix-ui';

import { Icon } from '../../components/Icon/Icon';
import { Image } from '../../components/Image/Image';
import { Layout } from '../../components/Layout/Layout';
import { Menu } from '../../components/Menu/Menu';
import { Text } from '../../components/Text/Text';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { IconType } from '../../types/IconType';
import { Option } from '../../types/Option';
import { Theme } from '../../types/Theme';

interface Props {
  tooltip: string;
  icon?: IconType;
  id?: string;
  isBordered?: boolean;
  menu?: {
    name: string;
    options: Option[];
    offsetX?: number;
    offsetY?: number;
  };
  mode?: 'icon' | 'image';
  text?: React.ReactNode;
  theme?: Theme;
  imgSrc?: string;
  onClick?: () => void;
}

// This component is specifically designed and does not fall under any existing EDS pattern.  Abstract it once the patterns are confirmed.
export const NavPill = ({
  icon,
  id,
  isBordered,
  menu,
  mode,
  text,
  theme = 'dark',
  tooltip,
  imgSrc,
  onClick,
}: Props) => {
  const styles = useStyles();

  const isDarkTheme = theme === 'dark';
  const isIconOnly = mode === 'icon';
  const isImageOnly = mode === 'image';
  // We need to check if the image is viable because we don't want to render an unsigned url.
  const isImageViable = Boolean(imgSrc);

  // When used as Menu trigger, use div to avoid invalid button-inside-button (Menu wraps trigger in a button)
  const triggerAs = menu ? 'div' : 'button';
  let content = (
    <Layout
      id={id}
      align="center"
      aria-label={isIconOnly ? text : undefined}
      as={triggerAs}
      className={isDarkTheme ? undefined : 'light-theme-nav-pill'}
      justify="center"
      spacing={1}
      styles={[
        isDarkTheme ? styles.interactionsInverse : undefined,
        componentStyles.pill({ isBordered, isImageOnly, isDarkTheme }),
      ]}
      onClick={menu ? () => onClick?.() : onClick}
      title={menu ? tooltip : undefined}
    >
      {icon && <Icon color="inherit" icon={icon} />}
      {isImageViable && (
        <Image
          src={imgSrc}
          width="35px"
          height="35px"
          borderRadius="50%"
          alt="avatar image"
        ></Image>
      )}
      {!isIconOnly && !(isImageOnly && isImageViable) && (
        <Text color="inherit">{text}</Text>
      )}
    </Layout>
  );

  if (tooltip && !menu) {
    content = <Tooltip tooltip={tooltip}>{content}</Tooltip>;
  }

  if (menu) {
    return <Menu {...menu} enablePortal={true} trigger={content} />;
  }

  return content;
};

const componentStyles = {
  pill: ({
    isBordered,
    isDarkTheme,
    isImageOnly,
  }: {
    isBordered?: boolean;
    isDarkTheme?: boolean;
    isImageOnly?: boolean;
  }) => {
    let backgroundColor: string;
    if (isDarkTheme) {
      backgroundColor = 'inverse.background';
    } else if (isImageOnly) {
      backgroundColor = 'background.secondary';
    } else {
      backgroundColor = 'background';
    }

    let borderColor: string;
    if (isDarkTheme) {
      borderColor = 'inverse.border';
    } else if (isImageOnly) {
      borderColor = 'border.divider';
    } else {
      borderColor = 'unset';
    }

    return {
      backgroundColor,
      border: isBordered ? 'border' : 'none',
      borderColor,
      borderRadius: 'pill',
      borderWidth: isDarkTheme ? 'm' : 's',
      color: isDarkTheme ? 'inverse.text.primary' : 'text.primary',
      cursor: 'pointer',
      height: 'height.m',
      minWidth: 'height.m',
      paddingLeft: isImageOnly ? 0 : 2,
      paddingRight: isImageOnly ? 0 : 2,
      '&:hover': {
        backgroundColor: isDarkTheme ? 'initial' : 'background.secondary',
      },
    };
  },
};
